/**
 * Notetaker App Roles on the shared IDC portal Entra app (plan NB-4). The names are confirmed by the
 * idc-portal registry (AIC-1473), and a rename is this one constant. They are not in tracker-api's /api/portal/me (it returns only
 * the Software Tracker roles), so the portal reads them from its own access token (notetakerRolesFromToken). That is for
 * showing or hiding links only: notetaker-api checks the roles itself on every request.
 */
export const NOTETAKER_ROLES = {
  admin: "Notetaker.Admin",
  user: "Notetaker.User",
} as const;

const ALL: readonly string[] = Object.values(NOTETAKER_ROLES);

export const isNotetakerAdmin = (roles: readonly string[]) => roles.includes(NOTETAKER_ROLES.admin);

/** Holds a role that opens Notetaker when the audience is "role" (AIC-1946): Notetaker.Admin needs no Notetaker.User. */
export const holdsNotetakerRole = (roles: readonly string[]) => roles.some((r) => ALL.includes(r));

/**
 * The few access-token claims the portal reads (AIC-1486), for showing and routing only: every role in `roles`, the
 * display `name` and `preferred_username` (or `upn`). null when the token is absent or unreadable. The token is not kept.
 */
export interface TokenClaims { roles: string[]; name: string; email: string }

export function tokenClaims(token: string | null): TokenClaims | null {
  const part = token?.split(".")[1];
  if (!part) return null;
  try {
    const bytes = Uint8Array.from(atob(part.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!payload || typeof payload !== "object") return null;
    const { roles, name, preferred_username: preferred, upn } = payload as { roles?: unknown; name?: unknown; preferred_username?: unknown; upn?: unknown };
    return {
      roles: Array.isArray(roles) ? roles.filter((r): r is string => typeof r === "string") : [],
      name: typeof name === "string" ? name : "",
      // preferred_username, else upn (v1 tokens), as notetaker-api reads it; never the `email` claim.
      email: typeof preferred === "string" ? preferred : typeof upn === "string" ? upn : "",
    };
  } catch {
    return null;
  }
}

/** The Notetaker roles in an access token's `roles` claim (never the rest of it). Unreadable or absent: none. */
export function notetakerRolesFromToken(token: string | null): string[] {
  return (tokenClaims(token)?.roles ?? []).filter((r) => ALL.includes(r));
}
